import React, { useState, useEffect } from "react";
import axios from "axios";
import {Form, Input, InputNumber, Button, Row, Col} from "antd";
import dayjs from "dayjs";
import { Select } from "antd";
const { Option } = Select;
const { TextArea } = Input;

const initialNewItem = {
    ItemName: "",
    UOM: "",
    Rate: 0,
    Quantity: 1,
};

const initialEditItem = {
    ItemName: "",
    UOM: "",
    Rate: 0,
    Quantity: 1,
    Total: 0,
};



export default function PurchaseOrder() {
    const [form] = Form.useForm();
    const [orders, setOrders] = useState([]);
    const [newItem, setNewItem] = useState(initialNewItem);
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [viewPurchaseOrder, setViewPurchaseOrder] = useState(null);
    const [editPurchaseOrder, setEditPurchaseOrder] = useState(null);

    const [filterDateType, setFilterDateType] = useState("=");
    const [filterDate, setFilterDate] = useState("");

    // Get all unique purchaseorder numbers
    const purchaseOrderNumberOptions = [...new Set(purchaseOrders.map(q => q.PurchaseOrderNumber).filter(Boolean))];
    // Filter state (multi-select for AntD Select)
    const [purchaseOrderNumberFilter, setPurchaseOrderNumberFilter] = useState([]); // or "" if single-select

    useEffect(() => {
        loadPurchaseOrders();
    }, []);

    const loadPurchaseOrders = () => {
        axios.get("/purchaseorder").then(res => setPurchaseOrders(res.data));
    };

    // delete purchaseOrder, from Actions list
    const handleDeletePurchaseOrder = (id) => {
        if (!window.confirm("Are you sure you want to delete this PurchaseOrder?")) return;
        axios.delete(`/purchaseorder/${id}`).then(() => {
            loadPurchaseOrders();
        });
    };

    const getValidTillDate = (date, validity) => {
        // date: String in "YYYY-MM-DD" or similar format
        // validity: Number of days
        if (!date || isNaN(validity)) return "";
        return dayjs(date).add(Number(validity || 0), "day").format("YYYY-MM-DD"); // or desired format
    };


    // Filtered purchaseOrder
    const filteredPurchaseOrders = purchaseOrders.filter(q => {
        if (purchaseOrderNumberFilter.length && !purchaseOrderNumberFilter.includes(q.PurchaseOrderNumber)) return false;
        if (filterDate) {
            const qDate = q.Date && q.Date.substr(0, 10);
            if (filterDateType === "=" && qDate !== filterDate) return false;
            if (filterDateType === "<" && qDate >= filterDate) return false;
            if (filterDateType === ">" && qDate <= filterDate) return false;
        }
        return true;
    });

    const resetFilters = () => {
        setPurchaseOrderNumberFilter([]);
        setFilterDateType("=");
        setFilterDate("");
        // Reset other filters here if you later add more (e.g., location, etc.)
    };


    // Add new item into Form.List 'Items'
    const handleAddNewItem = () => {
        if (!newItem.ItemName.trim()) {
            alert("Please enter item name");
            return;
        }
        if (newItem.Quantity < 1) {
            alert("Quantity must be at least 1");
            return;
        }
        const currentItems = form.getFieldValue("Items") || [];
        const total = (Number(newItem.Rate) || 0) * (Number(newItem.Quantity) || 0);

        form.setFieldsValue({
            Items: [...currentItems, {...newItem, Total: total}],
        });
        setNewItem(initialNewItem);
    };

    // Submit purchaseOrder form handler
    const onFinish = (values) => {
        if (!values.Items || values.Items.length === 0) {
            alert("Add at least one item before submitting.");
            return;
        }
        // Format date fields
        const valuesToSubmit = {...values};
        if (valuesToSubmit.Date) {
            valuesToSubmit.Date = valuesToSubmit.Date instanceof Date
                ? valuesToSubmit.Date.toISOString().substr(0, 10)
                : valuesToSubmit.Date;
        }
        if (valuesToSubmit.Quotation_Date) {
            valuesToSubmit.Quotation_Date = valuesToSubmit.Quotation_Date instanceof Date
                ? valuesToSubmit.Quotation_Date.toISOString().substr(0, 10)
                : valuesToSubmit.Quotation_Date;
        }
        axios.post("/purchaseorder", values).then(() => {
            form.resetFields();
            loadPurchaseOrders();
            setNewItem(initialNewItem);
        });
    };


    // Edit purchaseOrder Form
    function EditPurchaseOrderForm({purchaseOrder, onClose, onSave}) {
        const [editForm] = Form.useForm();

        useEffect(() => {
            if (purchaseOrder) {
                editForm.setFieldsValue({
                    Date: purchaseOrder.Date || new Date().toISOString().substr(0, 10),
                    Validity: purchaseOrder.Validity ?? 30,
                    Subject: purchaseOrder.Subject || "",
                    Description: purchaseOrder.Description || "",
                    Items: purchaseOrder.Items || [],
                });
            }
        }, [purchaseOrder, editForm]);

        const onEditFinish = (values) => {
            if (!values.Items || values.Items.length === 0) {
                alert("Add at least one item before saving.");
                return;
            }
            // Always recalculate Total for each item
            const itemsWithTotals = (values.Items || []).map(item => ({
                ...item,
                Rate: Number(item.Rate),
                Quantity: Number(item.Quantity),
                Total: Number(item.Rate) * Number(item.Quantity),
            }));

            // Send updated items to backend, always use itemsWithTotals
            axios.put(`/purchaseorder/${purchaseOrder.PurchaseOrderID}`, {
                ...values,
                Items: itemsWithTotals,
            }).then(() => {
                onSave({...values, Items: itemsWithTotals});
            });
        };

        //  -------- Edit  PurchaseOrder Form --------
        return (
            <div style={{border: "1px solid gray", padding: 20, background: "#FFF7F0", marginBottom: 20}}>
                <h3>Edit PurchaseOrder ID# {purchaseOrder.PurchaseOrderNumber}</h3>
                <Form form={editForm} onFinish={onEditFinish} layout="vertical" initialValues={purchaseOrder}>
                    {/* Vendor TextArea */}
                    <Form.Item
                        label="Vendor"
                        name="Vendor"
                        rules={[{required: true, message: "Vendor is required"}]}
                    >
                        <Input.TextArea rows={2}/>
                    </Form.Item>

                    {/* Ship_To TextArea */}
                    <Form.Item
                        label="Ship To"
                        name="Ship_To"
                        rules={[{required: true, message: "Ship To is required"}]}
                    >
                        <Input.TextArea rows={2}/>
                    </Form.Item>

                    {/* Date and Validity side by side */}
                    <Row gutter={16}>
                        <Col span={5}>
                            <Form.Item label="Date" name="Date" rules={[{required: true}]}>
                                <Input type="date"/>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="Validity"
                                label="Validity (days)"
                                rules={[{required: true, message: "Please enter validity"}]}
                            >
                                <InputNumber min={1} style={{width: "100%"}}/>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* New Fields */}
                    <Form.Item label="Quotation Date" name="Quotation_Date">
                        <Input type="date"/>
                    </Form.Item>
                    <Form.Item label="Quotation Ref" name="Quotation_Ref">
                        <Input/>
                    </Form.Item>
                    <Form.Item label="Sales Person" name="Sales_Person">
                        <Input/>
                    </Form.Item>
                    <Form.Item label="Requested By" name="Requested_by">
                        <Input/>
                    </Form.Item>
                    <Form.Item label="Ship Via" name="Ship_Via">
                        <Input/>
                    </Form.Item>
                    <Form.Item label="FOB" name="FOB">
                        <Input/>
                    </Form.Item>
                    <Form.Item label="Shipping Terms" name="Shipping_Terms">
                        <Input/>
                    </Form.Item>

                    {/* Special Instructions TextArea */}
                    <Form.Item label="Special Instructions" name="Special_Instructions">
                        <Input.TextArea rows={3}/>
                    </Form.Item>

                    {/* Items Editable Table remains unchanged */}
                    <Form.List name="Items">
                        {(fields, {remove}) => (
                            <table border={1} cellPadding={5}
                                   style={{width: "100%", borderCollapse: "collapse", marginBottom: 20}}>
                                <thead style={{backgroundColor: "#fff", fontWeight: "bold"}}>
                                <tr>
                                    <th>Sr</th>
                                    <th>Item Name</th>
                                    <th>UOM</th>
                                    <th>Rate</th>
                                    <th>Quantity</th>
                                    <th>Total</th>
                                    <th>Action</th>
                                </tr>
                                </thead>
                                <tbody>
                                {fields.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} style={{textAlign: "center", color: "#999"}}>
                                            No items added
                                        </td>
                                    </tr>
                                ) : fields.map(({key, name, ...restField}, index) => (
                                    <tr key={key}>
                                        <td>{index + 1}</td>
                                        <td>
                                            <Form.Item
                                                {...restField}
                                                name={[name, "ItemName"]}
                                                rules={[{required: true, message: "Missing item name"}]}
                                                style={{marginBottom: 0}}
                                            >
                                                <Input/>
                                            </Form.Item>
                                        </td>
                                        <td>
                                            <Form.Item {...restField} name={[name, "UOM"]} style={{marginBottom: 0}}>
                                                <Input/>
                                            </Form.Item>
                                        </td>
                                        <td>
                                            <Form.Item
                                                {...restField}
                                                name={[name, "Rate"]}
                                                rules={[{required: true, message: "Missing rate"}]}
                                                style={{marginBottom: 0}}
                                            >
                                                <InputNumber min={0} style={{width: "100%"}}/>
                                            </Form.Item>
                                        </td>
                                        <td>
                                            <Form.Item
                                                {...restField}
                                                name={[name, "Quantity"]}
                                                rules={[
                                                    {required: true, message: "Missing quantity"},
                                                    {type: "number", min: 1, message: "Quantity must be at least 1"},
                                                ]}
                                                style={{marginBottom: 0}}
                                            >
                                                <InputNumber min={1} style={{width: "100%"}}/>
                                            </Form.Item>
                                        </td>
                                        <td>
                                            <Form.Item shouldUpdate noStyle>
                                                {({getFieldValue}) => {
                                                    const rate = getFieldValue(["Items", name, "Rate"]) || 0;
                                                    const qty = getFieldValue(["Items", name, "Quantity"]) || 0;
                                                    return <span>{(rate * qty).toFixed(2)}</span>;
                                                }}
                                            </Form.Item>
                                        </td>
                                        <td>
                                            <Button onClick={() => remove(name)}>
                                                Delete
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        )}
                    </Form.List>

                    <Form.Item>
                        <Button type="default" htmlType="submit" style={{fontSize: "1.2em"}}>
                            Save
                        </Button>
                        <Button type="default" onClick={onClose} style={{marginLeft: 8}}>
                            Cancel
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        );
        }

        // -------- New Purchase Order Form--------
        return (
            <div style={{border: "1px solid gray", padding: 20, background: "#fff", marginBottom: 20}}>
                {/* New purchaseOrder Form */}
                <div
                    style={{
                        backgroundColor: "aliceblue",
                        border: "1px solid grey",
                        borderRadius: 6,
                        padding: 20,
                        marginBottom: 40,
                    }}
                >
                    <h3 style={{marginBottom: 16}}>Add PurchaseOrder</h3>
                    <Form
                        form={form}
                        layout="vertical"
                        initialValues={{
                            Validity: 30,
                            Date: new Date().toISOString().substr(0, 10),
                            Items: [],
                            Vendor: "",
                            Ship_To: "",
                            Quotation_Date: "",
                            Quotation_Ref: "",
                            Sales_Person: "",
                            Requested_by: "",
                            Ship_Via: "",
                            FOB: "",
                            Shipping_Terms: "",
                            Special_Instructions: "",
                        }}
                        onFinish={onFinish}
                    >
                        <Form.Item
                            label="Purchase Order Number"
                            name="PurchaseOrderNumber"
                            rules={[{ required: true, message: "Purchase Order Number is required" }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            label="Vendor"
                            name="Vendor"
                            rules={[{ required: true, message: "Vendor is required" }]}
                        >
                            <Input.TextArea rows={2} style={{ width: "100%" }} />
                        </Form.Item>
                        <Form.Item
                            label="Ship To"
                            name="Ship_To"
                            rules={[{ required: true, message: "Ship To is required" }]}
                        >
                            <Input.TextArea rows={2} style={{ width: "100%" }} />
                        </Form.Item>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item label="Quotation Date" name="Quotation_Date">
                                    <Input type="date" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item label="Quotation Ref" name="Quotation_Ref">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item label="Sales Person" name="Sales_Person">
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16} style={{ marginTop: 8 }}>
                            <Col span={8}>
                                <Form.Item label="Requested By" name="Requested_by">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item label="Ship Via" name="Ship_Via">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item label="FOB" name="FOB">
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16} style={{ marginTop: 8 }}>
                            <Col span={8}>
                                <Form.Item label="Shipping Terms" name="Shipping_Terms">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={16}>
                                <Form.Item label="Special Instructions" name="Special_Instructions">
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>


                        {/* New Item Inputs */}
                        <div style={{display: "flex", gap: 8, marginBottom: 16}}>
                            Item:
                            <Input
                                placeholder="Item Name"
                                value={newItem.ItemName}
                                onChange={e => setNewItem({...newItem, ItemName: e.target.value})}
                                style={{flex: 3}}
                            />
                            UOM:
                            <Input
                                placeholder="UOM"
                                value={newItem.UOM}
                                onChange={e => setNewItem({...newItem, UOM: e.target.value})}
                                style={{flex: 1}}
                            />
                            Rate:
                            <InputNumber
                                min={0}
                                placeholder="Rate"
                                value={newItem.Rate}
                                onChange={val => setNewItem({...newItem, Rate: val})}
                            />
                            QTY:
                            <InputNumber
                                min={1}
                                placeholder="Quantity"
                                value={newItem.Quantity}
                                onChange={val => setNewItem({...newItem, Quantity: val})}
                            />
                            <Button type="default" onClick={handleAddNewItem}>
                                Add Row
                            </Button>
                        </div>

                        {/* Items Editable Table */}
                        <Form.List name="Items">
                            {(fields, {remove}) => (
                                <table border={1} cellPadding={5}
                                       style={{width: "100%", borderCollapse: "collapse", marginBottom: 20}}>
                                    <thead style={{backgroundColor: "#fff", fontWeight: "bold"}}>
                                    <tr>
                                        <th>Sr</th>
                                        <th>Item Name</th>
                                        <th>UOM</th>
                                        <th>Rate</th>
                                        <th>Quantity</th>
                                        <th>Total</th>
                                        <th>Action</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {fields.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} style={{textAlign: "center", color: "#999"}}>No items
                                                added
                                            </td>
                                        </tr>
                                    ) : (
                                        fields.map(({key, name, ...restField}, index) => (
                                            <tr key={key}>
                                                <td>{index + 1}</td>
                                                <td><Form.Item {...restField} name={[name, "ItemName"]}
                                                               rules={[{required: true}]}
                                                               style={{marginBottom: 0}}><Input/></Form.Item></td>
                                                <td><Form.Item {...restField} name={[name, "UOM"]}
                                                               style={{marginBottom: 0}}><Input/></Form.Item></td>
                                                <td><Form.Item {...restField} name={[name, "Rate"]}
                                                               rules={[{required: true}]}
                                                               style={{marginBottom: 0}}><InputNumber min={0}
                                                                                                      style={{width: "100%"}}/></Form.Item>
                                                </td>
                                                <td><Form.Item {...restField} name={[name, "Quantity"]}
                                                               rules={[{required: true, type: "number", min: 1}]}
                                                               style={{marginBottom: 0}}><InputNumber min={1}
                                                                                                      style={{width: "100%"}}/></Form.Item>
                                                </td>
                                                <td>
                                                    <Form.Item shouldUpdate noStyle>
                                                        {({getFieldValue}) => {
                                                            const rate = getFieldValue(["Items", name, "Rate"]) || 0;
                                                            const qty = getFieldValue(["Items", name, "Quantity"]) || 0;
                                                            return <span>{(rate * qty).toFixed(2)}</span>;
                                                        }}
                                                    </Form.Item>
                                                </td>
                                                <td>
                                                    <Button danger onClick={() => remove(name)}>Delete</Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                    </tbody>
                                </table>
                            )}
                        </Form.List>

                        <Form.Item>
                            <Button type="default" htmlType="submit" style={{fontWeight: "bold", fontSize: "1em"}}>
                                Save PurchaseOrder
                            </Button>
                        </Form.Item>
                    </Form>
                  </div>



                  <div
                    style={{
                        background: "#FFFFEB",
                        border: "1px solid #beaedc",
                        padding: 18,
                        borderRadius: 6,
                    }}
                  >
                    <h3>PurchaseOrder List</h3>
                    <div style={{display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap"}}>
                        {/* PurchaseOrder Number Filter */}
                        <label style={{display: "flex", alignItems: "center"}}>
                            PurchaseOrder #:
                            <Select
                                showSearch
                                mode="multiple"
                                allowClear
                                placeholder="PurchaseOrder Number"
                                style={{marginLeft: 8, minWidth: 140}}
                                value={purchaseOrderNumberFilter.length ? purchaseOrderNumberFilter : undefined}
                                onChange={setPurchaseOrderNumberFilter}
                                filterOption={(input, option) =>
                                    option.children &&
                                    option.children.toLowerCase().startsWith(input.toLowerCase())
                                }
                            >
                                {purchaseOrderNumberOptions.map(num => (
                                    <Option key={num} value={num}>{num}</Option>
                                ))}
                            </Select>
                        </label>
                        <label>
                            Date:
                            <select
                                value={filterDateType}
                                onChange={e => setFilterDateType(e.target.value)}
                                style={{marginLeft: 8, width: 60}}
                            >
                                <option value="=">=</option>
                                <option value="<">&lt;</option>
                                <option value=">">&gt;</option>
                            </select>
                            <input
                                type="date"
                                value={filterDate}
                                onChange={e => setFilterDate(e.target.value)}
                                style={{marginLeft: 8, width: 150}}
                            />
                        </label>
                        <button
                            type="button"
                            onClick={() => {
                                setFilterDateType("=");
                                setFilterDate("");
                            }}
                            style={{padding: "6px 16px"}}
                        >
                            Clear Filters
                        </button>
                    </div>

                    <table
                        border={1}
                        cellPadding={6}
                        width="100%"
                        style={{borderCollapse: "collapse", fontSize: 14}}
                    >
                        <thead style={{backgroundColor: "#fff"}}>
                        <tr>
                            <th>PurchaseOrder#</th>
                            <th>Vendor</th>
                            <th>Ship To</th>
                            <th>Date</th>
                            <th>Validity</th>
                            <th># Items</th>
                            <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filteredPurchaseOrders.length === 0 ? (
                            <tr>
                                <td colSpan={7} style={{textAlign: "center", color: "#999"}}>
                                    No PurchaseOrder found
                                </td>
                            </tr>
                        ) : (
                            filteredPurchaseOrders.map((po) => (
                                <tr key={po.PurchaseOrderID}>
                                    <td>{po.PurchaseOrderNumber}</td>
                                    <td>{po.Vendor}</td>
                                    <td>{po.Ship_To || "-"}</td>
                                    <td>{po.Date}</td>
                                    <td>{po.Validity}</td>
                                    <td>{po.Items?.length || 0}</td>
                                    <td>
                                        <button
                                            onClick={() => setViewPurchaseOrder(po)}
                                            style={{
                                                marginRight: 8,
                                                backgroundColor: "lightgrey",
                                                color: "black",
                                                border: "none",
                                                padding: "5px 10px",
                                                borderRadius: "3px",
                                                cursor: "pointer",
                                            }}
                                        >
                                            View
                                        </button>
                                        <button
                                            onClick={() => setEditPurchaseOrder(po)}
                                            style={{
                                                marginRight: 8,
                                                backgroundColor: "lightgrey",
                                                color: "black",
                                                border: "none",
                                                padding: "5px 10px",
                                                borderRadius: "3px",
                                                cursor: "pointer",
                                            }}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDeletePurchaseOrder(po.PurchaseOrderID)}
                                            style={{
                                                marginRight: 8,
                                                backgroundColor: "lightgrey",
                                                color: "black",
                                                border: "none",
                                                padding: "5px 10px",
                                                borderRadius: "3px",
                                                cursor: "pointer",
                                            }}
                                        >
                                            Delete
                                        </button>
                                        <button
                                            onClick={() =>
                                                window.open(`/purchaseorder/${po.PurchaseOrderID}/pdf`, "_blank")
                                            }
                                            style={{
                                                marginRight: 8,
                                                backgroundColor: "lightgrey",
                                                color: "black",
                                                border: "none",
                                                padding: "5px 10px",
                                                borderRadius: "3px",
                                                cursor: "pointer",
                                            }}
                                        >
                                            PDF
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>

                </div>


                {/* View purchaseOrder Details */}
                {viewPurchaseOrder && (
                    <div style={{border: "1px solid gray", padding: 20, background: "#F4FFF0", marginBottom: 20}}>
                        <h3>PurchaseOrder Details #{viewPurchaseOrder.PurchaseOrderNumber}</h3>

                        <div><b>Vendor:</b> {viewPurchaseOrder.Vendor}</div>

                        {/* Ship_To replaces Location */}
                        <div><b>Ship To:</b> {viewPurchaseOrder.Ship_To}</div>

                        <div><b>Date:</b> {viewPurchaseOrder.Date}</div>
                        <div><b>Valid Till:</b> {getValidTillDate(viewPurchaseOrder.Date, viewPurchaseOrder.Validity)}
                        </div>

                        {/* Remove Subject and Description fields, Add new fields: */}
                        <div><b>Quotation Date:</b> {viewPurchaseOrder.Quotation_Date || "-"}</div>
                        <div><b>Quotation Ref:</b> {viewPurchaseOrder.Quotation_Ref || "-"}</div>
                        <div><b>Sales Person:</b> {viewPurchaseOrder.Sales_Person || "-"}</div>
                        <div><b>Requested By:</b> {viewPurchaseOrder.Requested_by || "-"}</div>
                        <div><b>Ship Via:</b> {viewPurchaseOrder.Ship_Via || "-"}</div>
                        <div><b>FOB:</b> {viewPurchaseOrder.FOB || "-"}</div>
                        <div><b>Shipping Terms:</b> {viewPurchaseOrder.Shipping_Terms || "-"}</div>

                        <div><b>Special Instructions:</b></div>
                        <pre>{viewPurchaseOrder.Special_Instructions || ""}</pre>

                        <h4>Items</h4>
                        <table border={1} cellPadding={4} style={{width: "100%"}}>
                            <thead>
                            <tr>
                                <th>Sr</th>
                                <th>Item Name</th>
                                <th>UOM</th>
                                <th>Rate</th>
                                <th>Quantity</th>
                                <th>Total</th>
                            </tr>
                            </thead>
                            <tbody>
                            {viewPurchaseOrder.Items.map((item, idx) => (
                                <tr key={idx}>
                                    <td>{idx + 1}</td>
                                    <td>{item.ItemName}</td>
                                    <td>{item.UOM}</td>
                                    <td>{item.Rate}</td>
                                    <td>{item.Quantity}</td>
                                    <td>{item.Total.toFixed(2)}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>

                        <button onClick={() => setViewPurchaseOrder(null)}
                                style={{
                                    marginRight: 8,
                                    backgroundColor: "lightgrey",
                                    color: "black",
                                    border: "none",
                                    padding: "5px 10px",
                                    borderRadius: "3px",
                                    cursor: "pointer"
                                }}>
                            Close
                        </button>
                        <button
                            onClick={() => window.open(`/purchaseorder/${viewPurchaseOrder.PurchaseOrderID}/pdf`, "_blank")}
                            style={{
                                marginRight: 8,
                                backgroundColor: "lightgrey",
                                color: "black",
                                border: "none",
                                padding: "5px 10px",
                                borderRadius: "3px",
                                cursor: "pointer"
                            }}>
                            PDF
                        </button>
                    </div>
                )}


                {/* Edit PurchaseOrder Modal */}
                {editPurchaseOrder && (
                    <EditPurchaseOrderForm
                        purchaseOrder={editPurchaseOrder}
                        onClose={() => setEditPurchaseOrder(null)}
                        onSave={() => {
                            loadPurchaseOrders();
                            setEditPurchaseOrder(null);
                            setViewPurchaseOrder(null);
                        }}
                    />
                )}

            </div>
        );
    }

